import { postDisplayText, summaryDisplayText, type SearchMarkup } from '../model/displayText';
import type {
  LibraryPost,
  LibraryPostPage,
  LibraryPostSummary,
  SharedLibraryPost,
} from '../model/types';

import type { paths } from '@/types/api';

type WireList =
  paths['/v2/community-posts']['get']['responses'][200]['content']['application/json']['data'];

type WireListedPost = WireList['posts'][number];

export type WirePost = NonNullable<
  paths['/v2/community-posts/{postId}']['get']['responses'][200]['content']['application/json']['data']
>;

type WireSharedPost = NonNullable<
  paths['/v2/public/community-posts/{token}']['get']['responses'][200]['content']['application/json']['data']
>;

type WireMarkup = WireListedPost['_highlightResult'];

/** The plain strings out of the search engine's per-field markup. */
const markupOf = (markup: WireMarkup): SearchMarkup => ({
  title: markup?.title?.value,
  body: markup?.body?.value,
});

/**
 * The anti-corruption layer: every wire post becomes a domain post here and
 * nowhere else. The search markup is resolved into display text and dropped, so
 * no screen can read it. Each return is checked with `satisfies`, so a schema
 * change that removes a field the domain needs fails to compile in this file.
 */
export function toLibraryPostSummary(wire: WireListedPost): LibraryPostSummary {
  const { _highlightResult, _snippetResult, ...post } = wire;

  return {
    ...post,
    ...summaryDisplayText(post, markupOf(_highlightResult), markupOf(_snippetResult)),
  } satisfies LibraryPostSummary;
}

export function toLibraryPostPage(wire: WireList): LibraryPostPage {
  return {
    ...wire,
    posts: wire.posts.map(toLibraryPostSummary),
  } satisfies LibraryPostPage;
}

/**
 * An API that predates inquiry library answers leaves `question` and `answers`
 * out. They map to the empty values the answers reader already treats as "draw
 * the body", so an older response renders exactly as it did.
 */
export function toLibraryPost(wire: WirePost): LibraryPost {
  const { _highlightResult, _snippetResult: _unusedSnippet, ...post } = wire;

  return {
    ...post,
    question: post.question ?? null,
    answers: post.answers ?? [],
    ...postDisplayText(post, markupOf(_highlightResult)),
  } satisfies LibraryPost;
}

export function toSharedLibraryPost(wire: WireSharedPost): SharedLibraryPost {
  const { _highlightResult: _unusedHighlight, _snippetResult: _unusedSnippet, ...post } = wire;

  return {
    ...post,
    question: post.question ?? null,
    answers: post.answers ?? [],
  } satisfies SharedLibraryPost;
}
